import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTour } from "./guideTourProvicer";
import { useLocation } from "react-router-dom";
import "./styles.css";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";

const DEFAULT_POP_W = 340;
const DEFAULT_POP_H = 160;

const TourGuide = () => {
    const { steps, current, setCurrent, active, setActive, hideGuideForever, canShowGuide, end } = useTour();
    const [pos, setPos] = useState({
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        popTop: 0,
        popLeft: 0,
        visible: false,
    });
    const [placement, setPlacement] = useState("bottom");
    const [arrowLeft, setArrowLeft] = useState(24);
    const [arrowTop, setArrowTop] = useState(24);
    const [animState, setAnimState] = useState("enter");
    const location = useLocation();
    const page = location.pathname;

    const maskIdRef = useRef(`tour-mask-${Math.random().toString(36).slice(2, 9)}`);
    const popRef = useRef(null);
    const watcherRef = useRef(null);
    const waitTimeoutRef = useRef(null);
    const showStepNumbers = true;

    useEffect(() => {
        if (!canShowGuide(page)) {
            setActive(false);
            setCurrent(null);
            setPos((p) => ({ ...p, visible: false }));
            return;
        }
    }, [page, active, canShowGuide, setActive, setCurrent]);

    const waitForSelector = (selector, timeout = 5000) => {
        return new Promise((resolve) => {
            if (!selector) return resolve(null);
            const existing = document.querySelector(selector);
            if (existing) return resolve(existing);

            const observer = new MutationObserver(() => {
                const el = document.querySelector(selector);
                if (el) {
                    observer.disconnect();
                    if (waitTimeoutRef.current) {
                        clearTimeout(waitTimeoutRef.current);
                        waitTimeoutRef.current = null;
                    }
                    resolve(el);
                }
            });

            observer.observe(document.documentElement, { childList: true, subtree: true });
            watcherRef.current = observer;

            waitTimeoutRef.current = setTimeout(() => {
                try { observer.disconnect(); } catch (e) {}
                resolve(null);
            }, timeout);
        });
    };

    const updatePos = async () => {
        if (!active || current === null || current >= steps.length) {
            setPos((p) => ({ ...p, visible: false }));
            return;
        }

        const sel = steps[current]?.selector;
        const el = sel ? document.querySelector(sel) : null;

        if (!el) {
            setPos((p) => ({ ...p, visible: false }));
            if (watcherRef.current) {
                try { watcherRef.current.disconnect(); } catch (e) {}
                watcherRef.current = null;
            }
            const found = await waitForSelector(sel, 6000);
            if (!found) {
                return;
            }
            const rectFound = found.getBoundingClientRect();
            try { found.scrollIntoView({ block: "center", behavior: "auto" }); } catch (e) {}
            setPos((p) => ({
                ...p,
                top: rectFound.top,
                left: rectFound.left,
                width: rectFound.width,
                height: rectFound.height,
                visible: true,
            }));
            requestAnimationFrame(() => computePopoverPlacement(rectFound));
            return;
        }

        const rect = el.getBoundingClientRect();
        setPos((p) => ({
            ...p,
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            visible: true,
        }));

        try { el.scrollIntoView({ block: "center", behavior: "auto" }); } catch (e) {}
        requestAnimationFrame(() => computePopoverPlacement(rect));
    };

    useEffect(() => {
        updatePos();
        const onWinChange = () => requestAnimationFrame(updatePos);
        window.addEventListener("resize", onWinChange);
        window.addEventListener("scroll", onWinChange, { passive: true });
        return () => {
            window.removeEventListener("resize", onWinChange);
            window.removeEventListener("scroll", onWinChange);
            if (watcherRef.current) {
                try { watcherRef.current.disconnect(); } catch (e) {}
                watcherRef.current = null;
            }
            if (waitTimeoutRef.current) {
                clearTimeout(waitTimeoutRef.current);
                waitTimeoutRef.current = null;
            }
        };
    }, [current, steps, active]);

    useEffect(() => {
        if (active && current !== null && current >= steps.length) {
            setActive(false);
        }
    }, [current, active, setActive]);

    if (current === null || current >= steps.length || !pos.visible) return null;
    const step = steps[current] || {};

    function rectsOverlap(a, b, padding = 0) {
        return !(
            a.left + a.width <= b.left - padding ||
            b.left + b.width <= a.left - padding ||
            a.top + a.height <= b.top - padding ||
            b.top + b.height <= a.top - padding
        );
    };

    function computePopoverPlacement(rect) {
        const pop = popRef.current;
        const gap = 12;
        const vpW = window.innerWidth;
        const vpH = window.innerHeight;
        const popW = pop ? pop.offsetWidth : DEFAULT_POP_W;
        const popH = pop ? pop.offsetHeight : DEFAULT_POP_H;

        if (!pop) {
            setTimeout(() => computePopoverPlacement(rect), 300);
            return;
        }

        const candidates = {
            right: { top: rect.top + rect.height / 2 - popH / 2, left: rect.right + gap, placement: "right" },
            left: { top: rect.top + rect.height / 2 - popH / 2, left: rect.left - popW - gap, placement: "left" },
            bottom: { top: rect.bottom + gap, left: rect.left + rect.width / 2 - popW / 2, placement: "bottom" },
            top: { top: rect.top - gap - popH, left: rect.left + rect.width / 2 - popW / 2, placement: "top" },
        };

        const normalize = (c) => {
            let top = Math.max(8, Math.min(c.top, vpH - popH - 8));
            let left = Math.max(8, Math.min(c.left, vpW - popW - 8));
            return { top, left, placement: c.placement };
        };

        const buildPopRect = (top, left) => ({ top, left, width: popW, height: popH });
        const targetRect = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };

        const order = ["bottom", "right", "left", "top"];
        let chosen = null;

        for (let key of order) {
            const c = normalize(candidates[key]);
            const popRect = buildPopRect(c.top, c.left);
            const isOverlap = rectsOverlap(popRect, targetRect, 8);
            const fitsHoriz = c.left >= 8 && c.left + popW <= vpW - 8;
            const fitsVert = c.top >= 8 && c.top + popH <= vpH - 8;
            if (!isOverlap && fitsHoriz && fitsVert) {
                chosen = c;
                break;
            }
        }

        if (!chosen) chosen = normalize(candidates[order[0]]);

        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        if (chosen.placement === "bottom" || chosen.placement === "top") {
            setArrowLeft(Math.min(Math.max(12, centerX - chosen.left), popW - 12));
        } else {
            setArrowTop(Math.min(Math.max(12, centerY - chosen.top), popH - 12));
        }

        setPos((prev) => ({ ...prev, popTop: chosen.top, popLeft: chosen.left }));
        setPlacement(chosen.placement);
    };

    const prev = async () => {
        if (current === 0) return;

        const prevStep = steps[current - 1];

        if (prevStep?.action) {
            await prevStep.action();
        }

        setAnimState("exit");
        setTimeout(() => {
            setCurrent((c) => (c > 0 ? c - 1 : c));
            setAnimState("enter");
        }, 180);
    };

    const next = async () => {
        const step = steps[current];

        if (step?.action) {
            await step.action();
        }

        if (current === steps.length - 1) {
            end();
            return;
        }
        setAnimState("exit");
        setTimeout(() => {
            setCurrent((c) => (c + 1 < steps.length ? c + 1 : c));
            setAnimState("enter");
        }, 180);
    };

    const finish = async () => {
        setAnimState("exit");
        if (step?.action) {
            await step.action();
        }
        setTimeout(() => {
            setCurrent(null);
            setPos((p) => ({ ...p, visible: false }));
            setActive(false);
            setAnimState("enter");
        }, 180);
    };

    const endForever = () => {
        setAnimState("exit");
        setTimeout(() => {
            setCurrent(null);
            setPos((p) => ({ ...p, visible: false }));
            hideGuideForever(page);
            setAnimState("enter");
        }, 180);
    };

    // Dynamic arrow positioning based on placement
    const getArrowStyle = () => {
        const baseStyle = {
            top: placement === "bottom"
                ? pos.popTop - 7
                : placement === "top"
                    ? pos.popTop + popRef.current?.offsetHeight - 7
                    : pos.top + pos.height / 2 - 7,
            left: placement === "left"
                ? pos.popLeft + popRef.current?.offsetWidth - 7
                : placement === "right"
                    ? pos.popLeft - 7
                    : pos.popLeft + arrowLeft - 7,
        };
        return baseStyle;
    };

    const overlayDom = (
        <>
            {/* SVG Overlay - Dark background with hole */}
            <svg
                className="tg-overlay"
                width="100%"
                height="100%"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    inset: 0,
                    zIndex: 999990,
                    pointerEvents: "auto",
                }}
            >
                <defs>
                    <mask id={maskIdRef.current}>
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        <rect
                            x={pos.left}
                            y={pos.top}
                            rx="8"
                            ry="8"
                            width={Math.max(0, pos.width)}
                            height={Math.max(0, pos.height)}
                            fill="black"
                        />
                    </mask>
                </defs>
                <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask={`url(#${maskIdRef.current})`} />
            </svg>

            {/* Highlight Border - Around target element */}
            <div
                className="tg-highlight"
                style={{
                    top: pos.top - 6,
                    left: pos.left - 6,
                    width: pos.width + 12,
                    height: pos.height + 12,
                }}
            />

            {/* Arrow/Pointer */}
            <div
                className="tg-arrow"
                style={getArrowStyle()}
            />

            {/* Popup Container */}
            {pos.popLeft != null && pos.popTop != null && pos.visible && (
                <div
                    ref={popRef}
                    role="dialog"
                    aria-modal="true"
                    className={`tg-pop ${animState === "enter" ? "tg-pop-enter" : "tg-pop-exit"}`}
                    style={{ left: pos.popLeft, top: pos.popTop }}
                >
                    {/* Header Section */}
                    <div className="p-4 pb-0">
                        <div className="tg-pop-header mb-4">
                            <h4>{step.title || `Step ${current + 1}`}</h4>
                            <div className="tg-pop-title-group">
                                {showStepNumbers && (
                                    <span className="tg-step-number">
                                        {current + 1} / {steps.length}
                                    </span>
                                )}
                                <button
                                    className="tg-btn-close"
                                    onClick={end ? endForever : finish}
                                    aria-label="Close tutorial"
                                    type="button"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="px-4">
                        <div className="tg-pop-content mb-4">
                            {typeof step.content === "function" ? step.content() : step.content}
                        </div>
                    </div>

                    {/* Footer Section - Buttons */}
                    <div className="p-4 pt-0">
                        <div className="tg-pop-footer">
                            <button
                                onClick={end ? endForever : finish}
                                className="tg-btn-skip"
                                type="button"
                            >
                                Skip
                            </button>
                            <div className="tg-pop-footer-right">
                                <button
                                    onClick={prev}
                                    disabled={current === 0}
                                    className={`tg-btn-secondary ${current === 0 ? "disabled" : ""}`}
                                    type="button"
                                >
                                    <faArrowLeft size={16} />
                                    <span>Sebelumnya</span>
                                </button>

                                {current === steps.length - 1 ? (
                                    <button
                                        onClick={end ? endForever : finish}
                                        className="tg-btn-primary"
                                        type="button"
                                    >
                                        Selesai
                                    </button>
                                ) : (
                                    <button
                                        onClick={next}
                                        className="tg-btn-primary"
                                        type="button"
                                    >
                                        <span>Selanjutnya</span>
                                        <faArrowRight size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return createPortal(overlayDom, document.body);
};

export default TourGuide;
